// Hyperliquid's `/info` reads the page makes itself (it answers CORS with `*`): candles for the
// chart, and where a wallet's USDC sits for a deposit.

import type { Candle } from "./feed";

export const INFO_URL = "https://api.hyperliquid.xyz/info";

async function info<T>(body: unknown, signal?: AbortSignal): Promise<T> {
  const response = await fetch(INFO_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) throw new Error(`Hyperliquid answered ${response.status}`);
  return (await response.json()) as T;
}

export const INTERVALS = ["1m", "5m", "15m", "1h", "4h", "1d"] as const;
export type Interval = (typeof INTERVALS)[number];

const INTERVAL_MS: Record<Interval, number> = {
  "1m": 60_000,
  "5m": 300_000,
  "15m": 900_000,
  "1h": 3_600_000,
  "4h": 14_400_000,
  "1d": 86_400_000,
};

/** The last `count` candles of `coin` at `interval`. */
export function candles(coin: string, interval: Interval, count = 500, signal?: AbortSignal): Promise<Candle[]> {
  const endTime = Date.now();
  const startTime = endTime - INTERVAL_MS[interval] * count;
  return info<Candle[]>({ type: "candleSnapshot", req: { coin, interval, startTime, endTime } }, signal);
}

/** How a Hyperliquid account keeps its balances, as `userAbstraction` answers. */
export type Abstraction = "default" | "disabled" | "unifiedAccount" | "portfolioMargin" | "dexAbstraction" | (string & {});

export type Funding = {
  abstraction: Abstraction;
  /** One pooled balance (unified or portfolio margin), which refuses `spotSend`. */
  pooled: boolean;
  /** Spot USDC free to send, or the pooled balance. Decimal, as Hyperliquid writes it. */
  spot: string;
  /** A standard account's withdrawable perp USDC. */
  perp: string;
};

type SpotState = { balances: { coin: string; total: string; hold: string }[] };
type PerpState = { withdrawable: string };

/** Where `user`'s USDC is on Hyperliquid and how the account keeps it. */
export async function funding(user: string, signal?: AbortSignal): Promise<Funding> {
  const [abstraction, spot, perp] = await Promise.all([
    info<Abstraction>({ type: "userAbstraction", user }, signal),
    info<SpotState>({ type: "spotClearinghouseState", user }, signal),
    info<PerpState>({ type: "clearinghouseState", user }, signal),
  ]);
  const usdc = spot.balances.find((balance) => balance.coin === "USDC");
  const free = usdc ? subtract(usdc.total, usdc.hold) : "0";
  return {
    abstraction,
    pooled: abstraction === "unifiedAccount" || abstraction === "portfolioMargin",
    spot: free,
    perp: perp.withdrawable ?? "0",
  };
}

/** `a - b` for two decimals Hyperliquid wrote, exactly, never below zero. */
function subtract(a: string, b: string): string {
  const places = Math.max(a.split(".")[1]?.length ?? 0, b.split(".")[1]?.length ?? 0);
  const raw = (text: string) => {
    const [whole = "0", fraction = ""] = text.split(".");
    return BigInt(whole + fraction.padEnd(places, "0"));
  };
  const diff = raw(a) - raw(b);
  if (diff <= 0n) return "0";
  const text = diff.toString().padStart(places + 1, "0");
  return places === 0 ? text : `${text.slice(0, -places)}.${text.slice(-places)}`;
}
